import type { AxiosResponse } from 'axios'
import axios from 'axios'

import {
  PatchDevice,
  Status,
  Transfer,
  TransferConcerns,
  DurationTestStatus,
  PcbPasswordResponse,
} from '@/api/definition'
import { samApiClient } from '@/api/index'
import type { Device } from '@/pages/Device/Device'
import * as Sentry from '@sentry/react'
export const getDeviceDetails = async (uid: Device['uid']): Promise<Device> => {
  try {
    const resp = await samApiClient.get<Device>(`device/${uid}`, {
      timeout: 10000,
    })
    return resp.data
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    throw error
  }
}

export const patchDevice = async (
  device: Device,
  toUpdate: PatchDevice,
): Promise<Device> => {
  try {
    const resp = await samApiClient.patch<Device>(
      `device/${device.uid}`,
      toUpdate,
      { timeout: 100000 },
    )

    // Merge existing device with updated fields from server response
    return {
      ...device,
      ...toUpdate,
      ...resp.data,
    }
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    throw error
  }
}

export const updateDeviceImei = async (
  uid: string | undefined,
  oldImei: string,
  newImei: string,
): Promise<AxiosResponse> => {
  try {
    return await samApiClient.patch(`device/${uid}/imei`, {
      OldImei: oldImei,
      NewImei: newImei,
    })
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    throw error
  }
}

export const updateDeviceUid = async (
  uid: string | undefined,
  serial: Device['serial'],
  newUid: string,
): Promise<AxiosResponse> => {
  try {
    return await samApiClient.patch(`device/${uid}/change-uid`, {
      Serial: serial,
      NewUid: newUid,
    })
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    throw error
  }
}

async function transferDurationTestStatus(
  uid: string,
  state: number,
  remarks: string = '',
): Promise<Transfer> {
  try {
    const response = await samApiClient.patch(
      `device/${uid}/in-duration-test?in_duration_test=${state}`,
      {
        in_duration_test: state,
        remarks,
      },
    )
    return { status: Status.SUCCESS, data: response.data }
  } catch (error) {
    if (!axios.isAxiosError(error) || !error.response) {
      return {
        status: Status.FAILURE,
        reason: 'app:device.transfer.noResponse',
      }
    }

    let reason = 'app:device.transfer.error'
    let concerns: undefined | TransferConcerns
    const { response } = error
    const { status, data } = response

    // Extract HTTP error details
    const httpErrorMessage = `Request failed with status code ${status}${
      typeof data === 'string' ? `: ${data}` : ''
    }`

    switch (status) {
      case 409:
        reason = 'app:device.transfer.offline'
        concerns = [{ message: httpErrorMessage }]
        break
      case 424:
        reason = 'app:device.transfer.blocked'
        concerns = [{ message: httpErrorMessage }]
        break
      case 502:
        reason = 'app:device.transfer.failed'
        concerns = [{ message: httpErrorMessage }]
        break
      case 503:
        reason = 'app:device.transfer.unable'
        concerns = [{ message: httpErrorMessage }]
        break
      case 504:
        reason = 'app:device.transfer.timeout'
        concerns = [{ message: httpErrorMessage }]
        break
      default:
        concerns = [{ message: httpErrorMessage }]
        Sentry.captureMessage('unsolvable transfer-status', (scope) => {
          scope.setExtra('response', response)
          return scope
        })
        break
    }

    return {
      status: Status.FAILURE,
      reason,
      concerns,
    }
  }
}

// Update your existing functions to use the transfer function
export const startDurationTest = async (
  deviceUids: string[],
): Promise<Transfer[]> => {
  const results: Transfer[] = []
  for (const uid of deviceUids) {
    const result = await transferDurationTestStatus(
      uid,
      DurationTestStatus.DurationTestInProgress,
    )
    results.push(result)
  }
  return results
}

export const setDurationTestStatus = async (
  deviceUids: string[],
  status: number,
  remarks: string = '',
): Promise<Transfer[]> => {
  const results: Transfer[] = []
  for (const uid of deviceUids) {
    const result = await transferDurationTestStatus(uid, status, remarks)
    results.push(result)
  }
  return results
}

export const stopDurationTest = async (deviceUids: string[]): Promise<void> => {
  // Use v1 endpoint: device/{uid}/in-duration-test?in_duration_test={status}
  for (const uid of deviceUids) {
    try {
      await samApiClient.patch(
        `device/${uid}/in-duration-test?in_duration_test=${DurationTestStatus.DurationTestCompleted}`,
        {
          in_duration_test: DurationTestStatus.DurationTestCompleted,
          remarks: '',
        },
      )
    } catch (error) {
      if (!axios.isCancel(error)) throw error
      throw error
    }
  }
}

export const getDurationTestResults = async (): Promise<AxiosResponse> => {
  try {
    return await samApiClient.get('device/duration-test/results')
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    throw error
  }
}

// Hook functions for Duration Test
export const useFetchDevicesForDurationTest = () => {
  const fetchDevicesForDurationTest = async () => {
    try {
      const response = await samApiClient.get('device/duration-test/available')
      return response.data
    } catch (error) {
      if (!axios.isCancel(error)) throw error
      throw error
    }
  }

  return { fetchDevicesForDurationTest }
}

export const useFetchDevicesInDurationTest = () => {
  const fetchDevicesInDurationTest = async () => {
    try {
      const response = await samApiClient.get(
        'device/duration-test/in-progress',
      )
      return response.data
    } catch (error) {
      if (!axios.isCancel(error)) throw error
      throw error
    }
  }

  return { fetchDevicesInDurationTest }
}

export const useFetchDurationTestResults = () => {
  const fetchDurationTestResults = async () => {
    try {
      // Align with V1: fetch from duration-test-reports with status filter
      const params = new URLSearchParams({
        page: '1',
        per_page: '100',
        not_in_service: '0', // inService: true
        self_test: '1',
        // 2 corresponds to the finish/verification state in V1 flows
        duration_test_status:
          DurationTestStatus.DurationTestVerify?.toString?.() || '2',
      })

      const response = await samApiClient.get(
        `duration-test-reports?${params.toString()}`,
      )
      return response.data
    } catch (error) {
      if (!axios.isCancel(error)) throw error
      throw error
    }
  }

  return { fetchDurationTestResults }
}

export const generatePcbPassword = async (
  uid: string,
): Promise<PcbPasswordResponse> => {
  try {
    const response = await samApiClient.post<PcbPasswordResponse>(
      'pcb-password-generator',
      { uid },
    )
    return response.data
  } catch (error) {
    if (!axios.isCancel(error)) throw error
    throw error
  }
}
